import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { toast } from "sonner";

const LOGIN_BG = "https://images.unsplash.com/photo-1531591022136-eb8b0da1e6d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgxMTc2MzI5fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [reason, setReason] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { identifier: identifier.trim(), reason });
      toast.success("Request submitted. Master Admin will contact you.");
      setForgotMode(false);
      setReason("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-background">
      {/* Left: Image / Brand */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 border-r border-border overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${LOGIN_BG})`,
            filter: "grayscale(0.3) contrast(1.05)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="relative z-10 flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-sm flex items-center justify-center text-white text-sm font-semibold"
            style={{ backgroundColor: "var(--theme-color)" }}
          >
            {branding.company_name?.charAt(0) || "C"}
          </div>
          <span className="text-base font-semibold tracking-tight">{branding.company_name}</span>
        </div>
        <div className="relative z-10 max-w-sm">
          <h1 className="text-3xl tracking-tight font-semibold mb-3">
            Sales, qualified.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            A single, structured workspace for your team — leads, pipeline, assignments and reporting.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div
              className="w-9 h-9 rounded-sm flex items-center justify-center text-white text-sm font-semibold"
              style={{ backgroundColor: "var(--theme-color)" }}
            >
              {branding.company_name?.charAt(0) || "C"}
            </div>
            <span className="text-base font-semibold">{branding.company_name}</span>
          </div>

          <p className="text-label mb-2">{forgotMode ? "Account Recovery" : "Sign in"}</p>
          <h2 className="text-2xl font-semibold tracking-tight mb-1">
            {forgotMode ? "Request password reset" : "Welcome back"}
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            {forgotMode
              ? "Master Admin will review your request."
              : "Use your email (Master Admin) or username."}
          </p>

          <form onSubmit={forgotMode ? submitForgot : submit} className="space-y-4">
            <div>
              <label className="text-label block mb-2">Email or Username</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                autoFocus
                placeholder="admin@company.com"
                data-testid="login-identifier-input"
                className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none transition-all placeholder:text-muted-foreground"
              />
            </div>

            {!forgotMode && (
              <div>
                <label className="text-label block mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    data-testid="login-password-input"
                    className="w-full bg-transparent border border-border rounded-sm px-3 py-2 pr-10 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Toggle password visibility"
                    data-testid="login-password-toggle"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {forgotMode && (
              <div>
                <label className="text-label block mb-2">Reason (optional)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Forgot password / locked out…"
                  data-testid="forgot-reason-input"
                  className="w-full bg-transparent border border-border rounded-sm px-3 py-2 text-sm focus:ring-1 focus:ring-[var(--theme-color)] focus:border-[var(--theme-color)] outline-none"
                />
              </div>
            )}

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid={forgotMode ? "forgot-submit-btn" : "login-submit-btn"}
              className="w-full flex items-center justify-center gap-1.5 text-white text-sm font-medium rounded-sm py-2.5 transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: "var(--theme-color)" }}
            >
              {loading ? "Please wait…" : forgotMode ? "Submit request" : "Sign in"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={() => { setForgotMode((m) => !m); setError(""); }}
              data-testid="toggle-forgot-btn"
              className="w-full text-xs text-muted-foreground hover:text-foreground"
            >
              {forgotMode ? "← Back to sign in" : "Forgot password?"}
            </button>
          </form>

          <p className="mt-10 text-xs text-muted-foreground text-center">
            Only Master Admin can create accounts.
          </p>
        </div>
      </div>
    </div>
  );
}
